import React, { useState, useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  X,
  Send,
  Loader2,
  Minimize2,
  Maximize2,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { useModelChat } from "../../hooks/useModels";
import ReactMarkdown from "react-markdown";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  insights?: string[];
  recommendations?: string[];
}

const SUGGESTED_QUESTIONS = [
  "Analyze Full Metrics",
  "Analyze the comparison",
  "What's this model's purpose?",
  "How can I improve it?",
  "Analyze my interactions",
  "Explain reinforcement learning",
];

const CHAT_STYLES = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(58, 130, 245, 0.3); }
    50% { box-shadow: 0 0 0 8px rgba(58, 130, 245, 0); }
  }
  
  .message-animate {
    animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .suggest-btn-hover:hover {
    background: linear-gradient(135deg, rgba(58, 130, 245, 0.08) 0%, rgba(99, 102, 241, 0.06) 100%);
    border-color: rgba(58, 130, 245, 0.3);
  }
  
  .glass-effect {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  .glass-effect-dark {
    background: rgba(15, 23, 42, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(148, 163, 184, 0.1);
  }
  
  .input-focus {
    border-color: rgb(58, 130, 245);
    box-shadow: 0 0 0 3px rgba(58, 130, 245, 0.1), 0 8px 32px rgba(58, 130, 245, 0.15);
  }
  
  .send-btn-pulse {
    animation: pulse-glow 2s infinite;
  }
`;

export const FloatingModelAssistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const chatMutation = useModelChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const match = location.pathname.match(/\/models\/([a-zA-Z0-9-]+)/);
    setModelId(match && match[1] ? match[1] : null);
  }, [location.pathname]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatMutation.isPending]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [input]);

  if (!location.pathname.startsWith("/models")) return null;

  const handleSend = async (content: string) => {
    if (!content.trim() || chatMutation.isPending || !modelId) return;

    const userMessage: ChatMessage = { role: "user", content };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    chatMutation.reset();

    try {
      const response = await chatMutation.mutateAsync({
        modelId,
        message: content,
      });

      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: response.message,
        insights: response.insights || undefined,
        recommendations: response.recommendations || undefined,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong. Try again." },
      ]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const isLoading = chatMutation.isPending;

  return (
    <>
      <style>{CHAT_STYLES}</style>
      <div className="fixed bottom-6 right-6 z-[999] flex flex-col items-end">
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full bg-white 
                       text-white shadow-xl hover:shadow-2xl
                       flex items-center justify-center
                       hover:scale-110 active:scale-95 transition-all duration-200
                       border border-[#263878]
                       group relative overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                            group-hover:translate-x-full transition-transform duration-500"
            />
            <img
              src="/quantumcue_icon.svg"
              alt="QuantumCue"
              className="w-6 h-6 relative z-10"
            />
          </button>
        )}

        {isOpen && (
          <div
            className={`
              flex flex-col overflow-hidden rounded-3xl
              transition-all duration-300 ease-out
              ${
                isMaximized
                  ? "fixed inset-6 lg:w-[700px] lg:left-auto lg:right-6 lg:bottom-6 lg:top-auto lg:h-[80vh]"
                  : "w-[450px] h-[640px]"
              }
              bg-white dark:bg-slate-950
              border border-slate-200/80 dark:border-slate-800/80
              shadow-2xl dark:shadow-slate-950/50
            `}
            style={{
              boxShadow: isMaximized
                ? "0 20px 60px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1)"
                : "0 25px 50px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 
                            border-b border-slate-200/50 dark:border-slate-800/50
                            bg-gradient-to-r from-slate-50/50 to-slate-50/30 
                            dark:from-slate-900/50 dark:to-slate-950/50
                            backdrop-blur-sm"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20">
                  <img
                    src="/quantumcue_icon.svg"
                    alt="QuantumCue"
                    className="w-5 h-5"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    QuantumCue
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    AI Assistant
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="p-2.5 rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-800/50 
                             transition-all duration-200 text-slate-600 dark:text-slate-400
                             hover:text-slate-900 dark:hover:text-slate-300"
                >
                  {isMaximized ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2.5 rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-800/50 
                             transition-all duration-200 text-slate-600 dark:text-slate-400
                             hover:text-slate-900 dark:hover:text-slate-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages Container */}
            <div
              className="flex-1 overflow-y-auto p-5 space-y-4 
                            bg-gradient-to-b from-slate-50 to-slate-50/50
                            dark:from-slate-950 dark:to-slate-950/50"
            >
              {!modelId ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4">
                  <div className="p-4 rounded-full bg-slate-200/50 dark:bg-slate-800/50">
                    <MessageSquare className="w-8 h-8 text-slate-400 dark:text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      No model selected
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                      Navigate to a model to continue
                    </p>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="space-y-3 mt-2">
                  {(() => {
                    const activeTab = searchParams.get("tab");
                    const isMetricsPage =
                      location.pathname.includes("/metrics") ||
                      activeTab === "metrics";
                    const isBenchmarksPage =
                      location.pathname.includes("/benchmarks") ||
                      activeTab === "benchmarks";

                    const suggestions = SUGGESTED_QUESTIONS.filter((q) => {
                      if (q === "Analyze Full Metrics") return isMetricsPage;
                      if (q === "Analyze the comparison")
                        return isBenchmarksPage;
                      return true;
                    });

                    return suggestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleSend(q)}
                        onMouseEnter={() => setHoveredIndex(i)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        className="w-full text-left text-sm px-5 py-4 rounded-2xl
                                   bg-white dark:bg-slate-900
                                   border border-slate-200 dark:border-slate-800
                                   text-slate-700 dark:text-slate-200
                                   transition-all duration-300
                                   group relative overflow-hidden
                                   suggest-btn-hover"
                        style={{
                          boxShadow:
                            hoveredIndex === i
                              ? "0 8px 24px rgba(58, 130, 245, 0.15), 0 0 0 1px rgba(58, 130, 245, 0.2)"
                              : "0 2px 8px rgba(0, 0, 0, 0.04)",
                          fontWeight:
                            i === 0 && (isMetricsPage || isBenchmarksPage)
                              ? 600
                              : 500,
                          color:
                            i === 0 && (isMetricsPage || isBenchmarksPage)
                              ? "#3a82f6"
                              : undefined,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span>{q}</span>
                          <Sparkles
                            className={`w-4 h-4 opacity-0 group-hover:opacity-100 
                                              transition-all duration-300 text-blue-500
                                              ${hoveredIndex === i ? "translate-x-1" : ""}`}
                          />
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 message-animate ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {message.role === "assistant" && (
                      <div className="flex-shrink-0 mt-1 p-2 rounded-lg bg-blue-500/10 dark:bg-blue-500/20">
                        <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                    )}
                    <div
                      className={`max-w-xs px-5 py-3.5 rounded-2xl text-sm leading-relaxed
                                  transition-all duration-300 ${
                                    message.role === "user"
                                      ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/30"
                                      : "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-slate-800/80 shadow-md dark:shadow-slate-950/40"
                                  }`}
                    >
                      {message.role === "user" ? (
                        message.content
                      ) : (
                        <div className="prose prose-sm dark:prose-invert max-w-none [&>*]:my-2">
                           <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}

              {isLoading && (
                <div className="flex items-start justify-start message-animate">
                  <div className="flex-shrink-0 mt-1 p-2 rounded-lg bg-blue-500/10 dark:bg-blue-500/20">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-blue-600 border-t-transparent 
                                    dark:border-blue-400 dark:border-t-transparent animate-spin"
                    />
                  </div>
                  <div
                    className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 
                                 rounded-2xl px-5 py-3.5 shadow-md dark:shadow-slate-950/40"
                  >
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-pulse" />
                      <div
                        className="w-2 h-2 rounded-full bg-slate-400 animate-pulse"
                        style={{ animationDelay: "0.2s" }}
                      />
                      <div
                        className="w-2 h-2 rounded-full bg-slate-400 animate-pulse"
                        style={{ animationDelay: "0.4s" }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Section */}
            <div
              className="p-4 border-t border-slate-200/50 dark:border-slate-800/50 
                            bg-gradient-to-t from-white to-white/50
                            dark:from-slate-950 dark:to-slate-950/50
                            backdrop-blur-sm"
            >
              <div
                className={`
                  flex items-end gap-3 px-4 py-2.5
                  rounded-2xl border-2
                  transition-all duration-300
                  ${
                    modelId
                      ? "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"
                      : "bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 opacity-50"
                  }
                `}
                onFocus={() => inputRef.current?.focus()}
              >
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => {
                    e.currentTarget.parentElement?.classList.add("input-focus");
                  }}
                  onBlur={(e) => {
                    e.currentTarget.parentElement?.classList.remove(
                      "input-focus",
                    );
                  }}
                  placeholder="Message QuantumCue..."
                  rows={1}
                  disabled={!modelId}
                  className="flex-1 resize-none bg-transparent text-sm text-slate-900 dark:text-white
                             outline-none border-0 focus:border-0 focus:ring-0
                             placeholder-slate-400 dark:placeholder-slate-500
                             max-h-24 py-1 px-0 min-h-0"
                />

                <button
                  onClick={() => handleSend(input)}
                  disabled={!input.trim() || !modelId || isLoading}
                  className={`flex items-center justify-center h-9 w-9 rounded-xl
                              transition-all duration-300 flex-shrink-0 relative
                              ${
                                input.trim() && modelId && !isLoading
                                  ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/40 hover:scale-105 active:scale-95"
                                  : "bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed"
                              }`}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {input.trim() && (
                        <div className="absolute inset-0 rounded-xl bg-white/20 animate-pulse" />
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingModelAssistant;
